/* Migration v3.3 (WF-004/005/055-065) · « Le parcours en clair » — one recap per visit, read by the builder's aside and
   its « Vérifier » card, and by the campaign page's « Ce que vos clients voient ». Three lines a visit: its number and
   what it gives (pack 2b, per-visit ruling P1: the visit's resolved gift — its own, or the campaign's), what it asks, when
   it asks (the visit's resolved order). Nothing here ties the gift to the answer: the moment line always ends in « facultative ». */
import { demandesOf, networksOf, platformLabel, socialLabel, socialSharesOf, type Countdown, type Demande, type Flow, type Shape } from '@/lib/campaign'

export interface VisitRecap { n: number; title: string; demande: string; moment: string | null; extra?: string }
export interface RecapCtx {
  /** the campaign's gift, as a name: the roue's, the direct gift's, or null for « sans cadeau » */
  giftName: string | null
  flow: Flow
  countdown: Countdown
  earlyOpen: boolean
  askName?: (id: string) => string | undefined
  /** pack 2b · visit i's resolved gift and order (lib/campaign › visitGiftOf / visitFlowOf); absent = the campaign's for every visit */
  perVisit?: (i: number) => { giftName: string | null; flow: Flow; countdown: Countdown }
}

/** « Avis · Google 70 % / Pages Jaunes 30 % », « Quiz · Nos produits », « Réseaux · Instagram » (split, point 5:
    « Réseaux · Instagram 70 % / TikTok 30 % », the review split's form), « Aucune demande · cadeau seul »
    — « Aucune demande · sans cadeau » when the visit gives nothing either (pack 2b: never « cadeau seul » with no gift). */
export function demandeRecap(d: Demande, askName?: (id: string) => string | undefined, hasGift = true): string {
  if (d.kind === 'rien') return hasGift ? 'Aucune demande · cadeau seul' : 'Aucune demande · sans cadeau'
  if (d.kind === 'avis') return `Avis · ${d.platforms.length > 1 ? d.platforms.map(p => `${platformLabel(p.platform)} ${p.share} %`).join(' / ') : platformLabel(d.platforms[0]?.platform ?? 'google')}`
  if (d.kind === 'suivre') { const ss = socialSharesOf(d); return `Réseaux · ${networksOf(d).map((so, k) => ss ? `${socialLabel(so)} ${ss[k]} %` : socialLabel(so)).join(' / ') || '…'}` }
  const n = d.askId ? askName?.(d.askId) : undefined
  return `${d.kind === 'quiz' ? 'Quiz' : d.kind === 'question' ? 'Question' : 'Retour privé'}${n ? ` · ${n}` : ''}`
}

/** When the demande shows, from the campaign's flow — the order is the campaign's (D-83), the same at every visit. */
export function momentRecap(d: Demande, ctx: RecapCtx): string | null {
  if (d.kind === 'rien') return null
  if (!ctx.giftName) return 'Sans cadeau · demande facultative, puis un merci'
  if (ctx.flow === 'demande_avant_roue') return 'Demande avant le cadeau · facultative'
  if (ctx.flow === 'cadeau_apres_invitation') return `Demande pendant la roue · ${ctx.countdown === 0 ? 'sans attente' : `${ctx.countdown} s maximum`} · facultative`
  return 'Demande après le cadeau · facultative'
}

export function visitRecaps(s: Shape, ctx: RecapCtx): VisitRecap[] {
  const ds = demandesOf(s)
  return ds.map((d, i) => {
    /* each visit reads its own resolved gift and order when the caller resolves per visit (V22), the campaign's otherwise */
    const v: RecapCtx = ctx.perVisit ? { ...ctx, ...ctx.perVisit(i) } : ctx
    return {
      n: i + 1,
      title: `Visite ${i + 1} · ${v.giftName ?? 'Sans cadeau'}`,
      demande: demandeRecap(d, v.askName, !!v.giftName),
      moment: momentRecap(d, v),
      /* list-2 #6 · no early-reveal line any more: the toggle left the builder (inert since D-88) */
    }
  })
}

/** The two closing lines under the recap: the visit rule, then what happens at the next authorised participation. */
export function recapTail(s: Shape, after: 'restart' | 'stop', hasGift: boolean): string[] {
  const multi = s.type === 'parcours' && s.steps.length > 1
  return [
    multi ? 'Chaque numéro correspond à une nouvelle visite autorisée, jamais à une étape supplémentaire dans la même visite.' : 'Une visite, une seule demande au maximum.',
    !multi || after === 'restart' ? 'À la prochaine participation autorisée : reprendre à la visite 1.'
      : hasGift ? 'Après la dernière visite : continuer les cadeaux sans demande.' : 'Après la dernière visite : plus de demande.',
  ]
}
